import { useEffect, useState } from "react";
import { useAxios } from "../../utils/useAxios";
import { MyInput } from "../../components/formFields/MyInput";
import { useForm, FieldError } from "react-hook-form";
import MySection from "../../components/formFields/MySection";
import Label from "../../components/form/Label";
import { MySelect } from "../../components/formFields/MySelect";
import { MyTextArea } from "../../components/formFields/MyTextArea";
import { useSnackbar } from "notistack";
import MyDatePicker from "../../components/formFields/MyDatePicker";
import { MyMultiSelect } from "../../components/formFields/MyMutiSelect";
import LocationAutoComplete from "../components/LocationAutoComplete/LocationAutoComplete";
import InfoModal from "../components/Info/InfoModal";
import { useNavigate, useSearchParams } from "react-router";
import Years from "../../components/formFields/MyYears";
import { CityAutocomplete } from "../../components/formFields/Mycity";
import { StateAutocomplete } from "../../components/formFields/Mystate";
import { bsDecrypt } from "../../utils/storage";
import MyFileUpload from "../../components/formFields/MyFileUpload";
import { MyCreateSelect } from "../../components/formFields/MyCreateSelect";
import { Switch } from "@mui/material";

interface FormColumn {
  field: string;
  type: "string" | "number" | "boolean";
}

interface FormData {
  [key: string]: any;
}

interface Props {
  getformData: any;
  editdata: any;
  listShow?: any;
  isFilterForm?: boolean;
}

interface Column {
  title: string;
  field: string;
  component: string;
  placeholder?: string;
}

interface Section {
  sectionid?: string | null;
  title?: string;
  columns: Column[];
}

interface FormState {
  formid?: string;
  sections?: Section[];
  columns?: any;
}

export default function FormBuilder({
  getformData,
  editdata = {},
  listShow,
  isFilterForm = false,
}: Props) {
  const [form, setForm] = useState<FormState>({});
  const [sections, setSections] = useState<Section[]>([]);
  const [columns, setColumns] = useState<Column[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalHeader, setModalHeader] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const decId = bsDecrypt(searchParams.get("id"));
  const isEditMode = Boolean(decId);
  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "onSubmit",
  });

  useEffect(() => {
    if (getformData) {
      setForm(getformData);
    }
  }, [getformData]);

  useEffect(() => {
    if (form?.sections) {
      setSections(form?.sections);
      setColumns(form?.columns || []);
    }
  }, [form?.sections]);

  const {
    res: formRes,
    mutate: formMutate,
    isPending: formIsPending,
  } = useAxios({
    url: "/api/auth/submitdata",
  });

  const onSubmit = (data: any) => {
    const formColumns =
      getformData?.columns?.length > 0
        ? getformData.columns
        : getformData?.sections?.flatMap((section: any) => section.columns) ||
          [];

    Object.keys(data).forEach((key) => {
      const fieldConfig = formColumns.find((col: any) => col.field === key);

      if (fieldConfig) {
        const isMultiSelect = fieldConfig.component === "select-multi";

        if (fieldConfig.type === "number") {
          if (isMultiSelect) {
            if (Array.isArray(data[key])) {
              data[key] = data[key].map((val: any) => Number(val));
            } else if (!isNaN(data[key]) && data[key] !== "") {
              data[key] = [Number(data[key])];
            } else {
              data[key] = [];
            }
          } else {
            if (
              data[key] === "" ||
              data[key] === undefined ||
              data[key] === null
            ) {
              data[key] = null;
            } else if (!isNaN(data[key])) {
              data[key] = Number(data[key]);
            }
          }
        }
      }
    });

    formMutate({
      formId: getformData?.formid,
      formData: data,
    });
  };

  useEffect(() => {
    if (formRes) {
      if (formRes?.data?.success) {
        enqueueSnackbar(formRes?.data?.message, {
          variant: "success",
        });

        if (form?.formid === "vehicle") {
          navigate("/fleet/vehicle");
        }
        if (form?.formid === "carrier") {
          navigate("/fleet/carrier");
        }
        if (form?.formid === "trailer") {
          navigate("/fleet/trailer");
        }

        listShow(true);
        reset();
      } else {
        enqueueSnackbar(formRes?.data?.message, {
          variant: "error",
        });
        listShow(false);
      }
    }
  }, [formRes]);

  const sectionsMerge: Section[] = [
    ...sections,
    { sectionid: null, title: "New Section", columns: columns },
  ];

  const {
    res: editRes,
    mutate: editMatate,
    isPending: editIsPending,
    error: editError,
  } = useAxios({
    url: "/api/auth/geteditdata",
  });

  useEffect(() => {
    if (decId) {
      if (form?.formid) {
        console.log("2 edit get data");
        editMatate({
          editId: Number(decId),
          formId: `${form?.formid}`,
          target: "edit",
        });
      }
    }
  }, [form?.formid]);

  useEffect(() => {
    if (editRes) {
      const data = editRes?.data?.data;

      if (data && Object.keys(data).length > 0) {
        reset({ ...data });
      }
    }
  }, [editRes, reset]);

  // useEffect(() => {
  //   if (editdata && Object.keys(editdata).length > 0) {
  //     reset({ ...editdata });
  //   }
  // }, [editdata, reset]);
  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      {sectionsMerge.map((section, index) => (
        <div key={index} className="mb-5">
          <MySection
            title={`${section?.sectionid ? section?.title : ""}`}
            sectionShow={section?.sectionid ? section?.title : ""}
          >
            <div
              className={`grid gap-4 ${getformData?.classes ? getformData?.classes : "grid-cols-3"}`}
            >
              {section.columns?.map((formfield, findex) => {
                const {
                  title,
                  field,
                  component,
                  placeholder,
                  options,
                  type,
                  info,
                  required,
                } = formfield as any;

                const isMandatory = required === 1 && true;

                const fieldSet = () => {
                  switch (component) {
                    case "input":
                      return (
                        <>
                          <Label>
                            {title}
                            {isMandatory && (
                              <span className="text-red-500"> *</span>
                            )}
                          </Label>
                          <MyInput
                            name={field}
                            register={register}
                            setValue={setValue}
                            error={errors[field] as FieldError | undefined}
                            placeholder={placeholder}
                            defaultValue={editdata[0]?.[field] ?? ""}
                            type={type}
                            disabled={isEditMode}
                            {...(isMandatory && {
                              validationRules: {
                                required: `${title} is required`,
                              },
                            })}
                          />
                        </>
                      );
                    case "location":
                      return (
                        <>
                          <Label>{title}</Label>
                          <LocationAutoComplete
                            name={field}
                            onSelect={({ name, lat, lng }) => {
                              setValue(field, [lat, lng]);
                            }}
                            register={register}
                            setValue={setValue}
                            error={errors?.[field] as FieldError | undefined}
                            disabled={isEditMode}
                          />
                        </>
                      );
                    case "select":
                      return (
                        <>
                          <Label className=" ">
                            {title}
                            {isMandatory && (
                              <span className="text-red-500"> *</span>
                            )}{" "}
                            {info && (
                              <button
                                onClick={() => {
                                  setModalContent(info);
                                  setModalHeader(title);
                                  setModalOpen(true);
                                }}
                                className="block text-white text-xs rounded-xl bg-indigo-500 hover:bg-indigo-800 focus:ring-4 focus:outline-none focus:ring-blue-300 px-2 py-0.3 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                                type="button"
                              >
                                <i className="fa fa-info"></i>
                              </button>
                            )}
                          </Label>
                          <MySelect
                            name={field}
                            control={control}
                            error={errors[field] as FieldError | undefined}
                            options={options || []}
                            type={type}
                            selected={
                              editdata?.[field]?.value ||
                              editdata?.[field] ||
                              ""
                            }
                            defaultValue={editdata[0]?.[field] ?? ""}
                            disabled={isEditMode}
                            {...(isMandatory && {
                              validationRules: {
                                required: `${title} is required`,
                              },
                            })}
                          />
                        </>
                      );
                    case "mycity":
                      return (
                        <>
                          <Label className=" ">
                            {title}
                            {isMandatory && (
                              <span className="text-red-500"> *</span>
                            )}{" "}
                          </Label>
                          <CityAutocomplete
                            name={field}
                            register={register}
                            error={errors[field] as FieldError | undefined}
                            selected={editdata[0]?.field}
                            disabled={isEditMode}
                          />
                        </>
                      );
                    case "mystate":
                      return (
                        <>
                          <Label className=" ">
                            {title}
                            {isMandatory && (
                              <span className="text-red-500"> *</span>
                            )}{" "}
                          </Label>
                          <StateAutocomplete
                            name={field}
                            register={register}
                            error={errors[field] as FieldError | undefined}
                            selected={editdata[0]?.field}
                            disabled={isEditMode}
                          />
                        </>
                      );
                    case "mycreateselect":
                      return (
                        <>
                          <Label className=" ">
                            {title}
                            {isMandatory && (
                              <span className="text-red-500"> *</span>
                            )}{" "}
                          </Label>
                          {/* <MyCreateSelect
                            name={field}
                            register={register}
                            error={errors[field] as FieldError | undefined}
                            selected={editdata[0]?.field}
                            disabled={isEditMode}
                          /> */}
                        </>
                      );
                    case "years":
                      return (
                        <>
                          <Label className=" ">
                            {title}
                            {isMandatory && (
                              <span className="text-red-500"> *</span>
                            )}{" "}
                            {info && (
                              <button
                                onClick={() => {
                                  setModalContent(info);
                                  setModalHeader(title);
                                  setModalOpen(true);
                                }}
                                className="block text-white text-xs rounded-xl bg-indigo-500 hover:bg-indigo-800 focus:ring-4 focus:outline-none focus:ring-blue-300 px-2 py-0.3 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                                type="button"
                              >
                                <i className="fa fa-info"></i>
                              </button>
                            )}
                          </Label>
                          <Years
                            name={field}
                            register={register}
                            error={errors[field] as FieldError | undefined}
                            type={type}
                            selected={
                              editdata[0]?.[field]?.value ||
                              editdata[0]?.[field] ||
                              ""
                            }
                            disabled={isEditMode}
                            {...(isMandatory && {
                              validationRules: {
                                required: `${title} is required`,
                              },
                            })}
                          />
                        </>
                      );
                    case "select-multi":
                      return (
                        <>
                          <Label>{title}</Label>
                          <MyMultiSelect
                            name={field}
                            register={register}
                            setValue={setValue}
                            error={errors[field] as FieldError | undefined}
                            options={options || []}
                            type={type}
                            selected={
                              Array.isArray(editdata[0]?.[field])
                                ? editdata[0]?.[field]
                                : editdata[0]?.[field] !== undefined
                                  ? [editdata[0][field]]
                                  : []
                            }
                            disabled={isEditMode}
                          />
                        </>
                      );
                    case "textarea":
                      return (
                        <>
                          <Label>{title}</Label>
                          <MyTextArea
                            name={field}
                            register={register}
                            error={errors[field] as FieldError | undefined}
                            placeholder={placeholder}
                            defaultValue={editdata?.[field] ?? null}
                            disabled={isEditMode}
                          />
                        </>
                      );
                    case "checkbox":
                      return (
                        <div className="flex items-center">
                          <Switch />
                          <label
                            htmlFor={field}
                            className="w-full py-1 ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                          >
                            {title}
                          </label>
                        </div>
                      );
                    case "datepicker":
                      return (
                        <>
                          <Label>{title}</Label>
                          <MyDatePicker
                            name={field}
                            control={control}
                            disabled={isEditMode}
                          />
                        </>
                      );
                    case "fileupload":
                      return (
                        <>
                          <Label>{title}</Label>
                          <MyFileUpload
                            name="documents"
                            register={register}
                            error={errors[field] as FieldError | undefined}
                            label="Upload Documents"
                            required={true}
                            multiple={true}
                            maxSize={10} // 10MB
                          />
                        </>
                      );
                    default:
                      return <></>;
                  }
                };

                return <div key={`${field}-${findex}`}>{fieldSet()}</div>;
              })}
            </div>
          </MySection>
        </div>
      ))}
      <div className="flex border-t pt-3 justify-end gap-2">
        <button
          type="reset"
          className="rounded bg-gray-200 px-6 py-2 text-black font-medium hover:bg-gray-300"
          disabled={isEditMode}
        >
          Reset
        </button>
        <button
          type="submit"
          className="rounded bg-indigo-500 px-6 py-2 text-white font-medium hover:bg-indigo-700"
          disabled={isEditMode}
        >
          {formIsPending ? "Submitting" : "Submit"}
        </button>
      </div>
      <InfoModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalHeader}
        content={modalContent}
      />
    </form>
  );
}
